import { useQuery } from "@tanstack/react-query";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";
import { formatSocialTokenAge } from "@/utils/formatSocialTokenAge";

export interface DisplayCoin {
  id: string; name: string; ticker: string; creator: string; marketCap: string;
  volume: string; change24h: string; replies: number; holders: number; timeAgo: string;
  description: string; coinAddress?: string; legacyId?: string;
  transactionHash?: string;
}

export const fallbackCoins: DisplayCoin[] = [
  { id: "creator-dao", name: "Creator DAO", ticker: "$CDAO", creator: "F0UR", marketCap: "$48.7K", volume: "$8.2K", change24h: "—", replies: 26, holders: 84, timeAgo: "37m ago", description: "A community token for digital creators that unlocks exclusive content and collaborative opportunities." },
  { id: "music-collective", name: "Music Collective", ticker: "$MUSC", creator: "HHJpu", marketCap: "$1.4B", volume: "$92K", change24h: "—", replies: 4659, holders: 1204, timeAgo: "1 month ago", description: "Supporting independent musicians with community funding, royalty sharing, and fan engagement tools." },
  { id: "art-alliance", name: "Art Alliance", ticker: "$ARTS", creator: "EZX7G1", marketCap: "$798.9M", volume: "$41K", change24h: "—", replies: 2635, holders: 711, timeAgo: "2 months ago", description: "Connecting digital artists with collectors through token-gated galleries and collaborative events." },
];

const money = (value: number | null | undefined) => value == null ? "$—" : `$${value >= 1000000 ? `${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(0)}`;

export function useCoins(limit = 50) {
  return useQuery({
    queryKey: ["production-coins", limit],
    queryFn: async () => {
      const safeQuery = async (query: PromiseLike<any>) => {
        try { return await query; } catch { return { data: [] }; }
      };
      const [metricsResult, createdCoinsResult, fairLaunchResult] = await Promise.all([
        safeQuery((supabaseExtended as any).from("coin_metrics").select("*").order("total_volume", { ascending: false, nullsFirst: false }).limit(limit)),
        safeQuery((supabaseExtended as any).from("created_coins").select("*").order("created_at", { ascending: false }).limit(limit)),
        safeQuery((supabaseExtended as any).from("fair_launch_coins").select("*").order("created_at", { ascending: false }).limit(limit)),
      ]);
      const metricData = metricsResult.data;
      const createdCoins = createdCoinsResult.data;
      const fairLaunchCoins = fairLaunchResult.data;
      const data = [...(metricData || []), ...(createdCoins || []), ...(fairLaunchCoins || [])].filter((coin: any, index: number, all: any[]) => index === all.findIndex((candidate) => (candidate.coin_address || candidate.contract_address || candidate.id) === (coin.coin_address || coin.contract_address || coin.id)));
      if (!data.length) return { coins: fallbackCoins, isFallback: true };
      return { isFallback: false, coins: data.map((coin: any): DisplayCoin => ({
        id: coin.coin_address || coin.contract_address || coin.id, name: coin.name || "Unnamed Community",
        ticker: `$${(coin.symbol || coin.name || "COIN").replace(/[^a-z0-9]/gi, "").slice(0, 6).toUpperCase()}`,
        creator: (coin.creator_address || coin.address) ? `${(coin.creator_address || coin.address).slice(0, 6)}…` : "unknown",
        marketCap: money(coin.market_cap ?? coin.total_value_locked), volume: money(coin.total_volume), change24h: coin.change_24h ?? coin.price_change_24h ?? "—",
        replies: Number(coin.reply_count || 0), holders: Number(coin.holder_count || 0),
        timeAgo: formatSocialTokenAge(coin.last_trade_timestamp || coin.created_at),
        description: coin.description || "Community token on Polygon.", coinAddress: coin.coin_address || coin.contract_address,
        transactionHash: coin.transaction_hash || coin.transactionHash, legacyId: coin.id,
      })) };
    }, staleTime: 60_000,
  });
}
